var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React from "react";
import { useParams } from "react-router-dom";
import { nanoid } from "nanoid";
import { useProgram } from "frontend/src/domain/programs";
import { findLastIndex } from "frontend/src/lib/findLastIndex";
import { usePlayerTrackId } from "frontend/src/domain/player-track-queue";
import { useIsAudioPlaying } from "frontend/src/domain/audio";
import { useViewerAdvertisements } from "frontend/src/domain/audio-advertisements";
import { pause, resume } from "frontend/src/features/control-audio";
import { playUserFilesAttempted } from "frontend/src/features/play-user-files";
import { useUpdateProgram } from "frontend/src/domain/programs/actions";
export var usePageModel = function () {
    var id = useParams().id;
    var programId = Number(id);
    return useProgram(programId);
};
var useEditProgramTitle = function (initialTitle) {
    var _a = __read(React.useState(initialTitle), 2), title = _a[0], setTitle = _a[1];
    var _b = __read(React.useState(null), 2), error = _b[0], setError = _b[1];
    var validate = function () {
        if (title.length === 0) {
            setError("Введите название программы.");
            return false;
        }
        return true;
    };
    return { value: title, set: setTitle, validate: validate, error: error };
};
var useEditProgramEntries = function (initialEntries) {
    var _a = __read(React.useState(initialEntries.map(function (entry) { return ({
        id: nanoid(),
        data: entry,
        errors: {},
    }); })), 2), entries = _a[0], setEntries = _a[1];
    var validate = function () {
        var isValid = true;
        setEntries(entries.map(function (entry) {
            return __assign(__assign({}, entry), { errors: {} });
        }));
        entries.forEach(function (entry, index) {
            if (entry.data.type === "playlist") {
                var errors_1 = {};
                if (entry.data.playlistId === -1) {
                    errors_1.playlistNotSelected = "Выберите плейлист.";
                    isValid = false;
                }
                if (entry.data.duration === 0 && entry.data.trackCount === 0) {
                    errors_1.durationOrTrackCountNotSelected =
                        "Выберите кол-во треков или продолжительность.";
                    isValid = false;
                }
                if (!isValid) {
                    setEntries(entries.map(function (entry, idx) {
                        if (index === idx) {
                            return __assign(__assign({}, entry), { errors: errors_1 });
                        }
                        return entry;
                    }));
                }
            }
        });
        return isValid;
    };
    var trackCountMap = entries.reduce(function (acc, entry) {
        var _a;
        if (entry.data.type !== "track")
            return acc;
        var id = entry.data.trackId;
        var value = id in acc ? acc[id] + 1 : 1;
        return __assign(__assign({}, acc), (_a = {}, _a[id] = value, _a));
    }, {});
    var addTrack = function (track) {
        setEntries(entries.concat({
            id: nanoid(),
            data: {
                id: -1,
                type: "track",
                trackId: track.id,
                playOrder: "single",
                track: track,
            },
            errors: {},
        }));
    };
    var addPlaylist = function () {
        setEntries(entries.concat({
            id: nanoid(),
            data: {
                id: -1,
                type: "playlist",
                playOrder: "forward",
                duration: 0,
                trackCount: 0,
                playlistId: -1,
            },
            errors: {},
        }));
    };
    var removeByIndex = function (index) {
        return setEntries(entries.filter(function (_, idx) { return idx !== index; }));
    };
    var removeLastById = function (id) {
        var index = findLastIndex(entries, function (entry) { return entry.data.id === id; });
        removeByIndex(index);
    };
    var changeTrackCount = function (trackCount, index) {
        setEntries(entries.map(function (entry, idx) {
            if (idx === index && entry.data.type === "playlist") {
                return __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { trackCount: trackCount }) });
            }
            return entry;
        }));
    };
    var changePlaylist = function (playlistId, index) {
        setEntries(entries.map(function (entry, idx) {
            if (idx === index && entry.data.type === "playlist") {
                return __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { playlistId: playlistId }) });
            }
            return entry;
        }));
    };
    var changeDuration = function (duration, index) {
        setEntries(entries.map(function (entry, idx) {
            if (idx === index && entry.data.type === "playlist") {
                return __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { duration: duration }) });
            }
            return entry;
        }));
    };
    var changePlayOrder = function (playOrder, index) {
        setEntries(entries.map(function (entry, idx) {
            if (idx === index && entry.data.type === "playlist") {
                return __assign(__assign({}, entry), { data: __assign(__assign({}, entry.data), { playOrder: playOrder }) });
            }
            return entry;
        }));
    };
    return {
        addTrack: addTrack,
        addPlaylist: addPlaylist,
        removeByIndex: removeByIndex,
        removeLastById: removeLastById,
        changeTrackCount: changeTrackCount,
        changeDuration: changeDuration,
        changePlayOrder: changePlayOrder,
        changePlaylist: changePlaylist,
        trackCountMap: trackCountMap,
        items: entries,
        validate: validate,
    };
};
export var useEditProgram = function (program) {
    var title = useEditProgramTitle(program.title);
    var entries = useEditProgramEntries(program.entries);
    var update = useUpdateProgram();
    var submit = function (evt) {
        evt.preventDefault();
        if (title.validate() && entries.validate()) {
            update.run({
                id: program.id,
                title: title.value,
                entries: entries.items.map(function (entry) { return entry.data; }),
            });
        }
    };
    return { title: title, entries: entries, isUpdating: update.isLoading, submit: submit };
};
export var useViewerAddableAudioAdvertisementList = function (_a) {
    var _b, _c;
    var tracks = _a.tracks;
    var playerTrackId = usePlayerTrackId();
    var isAudioPlaying = useIsAudioPlaying();
    var advertisements = useViewerAdvertisements();
    var items = (_b = advertisements.data) === null || _b === void 0 ? void 0 : _b.map(function (_a, index, array) {
        var _b;
        var id = _a.id, title = _a.title, duration = _a.duration, url = _a.url;
        var selected = id === playerTrackId;
        var playing = selected && isAudioPlaying;
        var paused = selected && !isAudioPlaying;
        var play = function () {
            return selected
                ? resume()
                : playUserFilesAttempted({
                    tracks: array,
                    startAtTrackIndex: index,
                });
        };
        var add = function () {
            return tracks.add({
                type: "private",
                id: id,
                title: title,
                duration: {
                    seconds: duration.seconds,
                    formatted: duration.formatted,
                },
                url: url,
            });
        };
        var remove = function () { return tracks.remove(id); };
        return {
            id: id,
            title: title,
            duration: duration.formatted,
            selected: selected,
            loading: false,
            playing: playing,
            paused: paused,
            addCount: (_b = tracks.countMap[id]) !== null && _b !== void 0 ? _b : 0,
            actions: {
                add: add,
                remove: remove,
                play: play,
                pause: pause,
            },
        };
    });
    var empty = (_c = (items === null || items === void 0 ? void 0 : items.length) === 0) !== null && _c !== void 0 ? _c : false;
    return { empty: empty, items: items };
};
